import { Stat } from '@salonomy/ui';
import { usePaymentCostComposition, type ReportRange } from '../../data/reports.js';
import { exportCsvFilename } from '../../lib/csv.js';
import { money, moneyWhole, percentFromBps, pluralize } from '../../lib/format.js';
import { ReportCard, type CsvExport } from './shared.js';

/**
 * WS9 — payment-cost transparency (Overview 6.10): platform fee + Stripe interchange vs the net the salon
 * keeps, with the effective rate and a per-method breakdown. MANAGER-ONLY on the API → the caller passes the
 * isFloorManager gate as `enabled`; a non-manager gets an honest note, never a guaranteed 403. The effective
 * rate is server-computed basis points (percentFromBps); every fee/net figure is the server's (ADR-0009).
 */

const METHOD_LABEL: Record<string, string> = {
  card: 'Card',
  cash: 'Cash',
  terminal: 'Terminal',
  online: 'Online',
  voucher: 'Voucher',
  gift_card: 'Gift card',
};

function methodLabel(method: string): string {
  return METHOD_LABEL[method] ?? method;
}

export function PaymentCostCompositionCard({
  range,
  enabled,
}: {
  range: ReportRange;
  enabled: boolean;
}) {
  const { data: report, isLoading, isError } = usePaymentCostComposition(range, enabled);

  if (!enabled) {
    return (
      <ReportCard
        title="Payment cost composition"
        subtitle="Platform fee and card interchange vs the net the salon keeps."
        isEmpty
        emptyLabel="Payment cost composition is available to salon owners and managers."
      />
    );
  }

  const isEmpty = !!report && report.byMethod.length === 0;

  const csv: CsvExport | null =
    report && report.byMethod.length > 0
      ? {
          filename: exportCsvFilename('payment-cost-composition'),
          headers: ['Method', 'Gross', 'Fees', 'Payments'],
          rows: report.byMethod.map((m) => [
            methodLabel(m.method),
            money(m.grossPence),
            money(m.feePence),
            m.count,
          ]),
        }
      : null;

  return (
    <ReportCard
      title="Payment cost composition"
      subtitle="What card processing costs the salon over this period — platform fee + interchange vs net. Manager-only, server-computed."
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      emptyLabel="No card payments in this period."
      csv={csv}
    >
      {report ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Stat label="Gross" value={moneyWhole(report.grossPence)} />
            <Stat label="Net to salon" value={moneyWhole(report.netToSalonPence)} />
            <Stat label="Effective rate" value={percentFromBps(report.effectiveRateBps)} />
          </div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            {money(report.platformFeePence)} platform fee · {money(report.stripeFeePence)} card
            interchange
          </div>

          <div className="settings-list">
            {report.byMethod.map((m) => (
              <div key={m.method} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{methodLabel(m.method)}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {pluralize(m.count, 'payment')} · {money(m.grossPence)} gross
                  </div>
                </div>
                <Stat label="Fees" value={money(m.feePence)} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </ReportCard>
  );
}
